import { create } from 'zustand';
import type { NetworkStats, NodeInfo, TimeCrystal, CreditBalance } from '../types';
import { edgeNetService } from '../services/edgeNet';
import { storageService } from '../services/storage';
import { relayClient, type TaskAssignment, type NetworkState as RelayNetworkState } from '../services/relayClient';
import { firebaseDataService, type NetworkStatistics, type PeerInfo } from '../services/firebaseData';
import { useIdentityStore } from './identityStore';

interface ContributionSettings {
  enabled: boolean;
  cpuLimit: number;
  gpuEnabled: boolean;
  gpuLimit: number;
  memoryLimit: number;
  bandwidthLimit: number;
  respectBattery: boolean;
  onlyWhenIdle: boolean;
  idleThreshold: number;
  consentGiven: boolean;
  consentTimestamp: Date | null;
}

interface NetworkState {
  stats: NetworkStats;
  nodes: NodeInfo[];
  timeCrystal: TimeCrystal;
  credits: CreditBalance;
  isConnected: boolean;
  isRelayConnected: boolean;
  isLoading: boolean;
  error: string | null;
  startTime: number;
  contributionSettings: ContributionSettings;
  isWASMReady: boolean;
  nodeId: string | null;
  // Relay network state
  relayNetworkState: RelayNetworkState | null;
  connectedPeers: string[];
  pendingTasks: TaskAssignment[];
  // Persisted cumulative values from IndexedDB
  persistedCredits: number;
  persistedTasks: number;
  persistedUptime: number;
  // Firebase real-time data
  isFirebaseConnected: boolean;
  isDemoMode: boolean;
  firebasePeers: PeerInfo[];
  firebaseStats: NetworkStatistics | null;
  // Pending contribution credits (earned locally, not yet synced to QDAG)
  pendingContributionCredits: number;
  lastContributionSyncTime: number;
  contributionSyncInterval: number; // 30 seconds default

  setStats: (stats: Partial<NetworkStats>) => void;
  addNode: (node: NodeInfo) => void;
  removeNode: (nodeId: string) => void;
  updateNode: (nodeId: string, updates: Partial<NodeInfo>) => void;
  setTimeCrystal: (crystal: Partial<TimeCrystal>) => void;
  setCredits: (credits: Partial<CreditBalance>) => void;
  setConnected: (connected: boolean) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  updateRealStats: () => void;
  getUptime: () => number;
  setContributionSettings: (settings: Partial<ContributionSettings>) => void;
  giveConsent: () => void;
  revokeConsent: () => void;
  initializeEdgeNet: () => Promise<void>;
  startContributing: () => void;
  stopContributing: () => void;
  saveToIndexedDB: () => Promise<void>;
  loadFromIndexedDB: () => Promise<void>;
  connectToRelay: () => Promise<boolean>;
  disconnectFromRelay: () => void;
  processAssignedTask: (task: TaskAssignment) => Promise<void>;
  // Firebase methods
  connectToFirebase: () => Promise<boolean>;
  disconnectFromFirebase: () => void;
  setFirebaseStats: (stats: NetworkStatistics) => void;
  addFirebasePeer: (peer: PeerInfo) => void;
  removeFirebasePeer: (peerId: string) => void;
  // Clear all local data
  clearLocalData: () => void;
  // Contribution sync methods
  syncContributionToRelay: () => Promise<void>;
  addPendingContribution: (amount: number) => void;
}

const initialStats: NetworkStats = {
  totalNodes: 0,
  activeNodes: 0,
  totalCompute: 0,
  creditsEarned: 0,
  tasksCompleted: 0,
  uptime: 0,
  latency: 0,
  bandwidth: 0,
};

const initialTimeCrystal: TimeCrystal = {
  phase: 0,
  frequency: 1.618,
  coherence: 0,
  entropy: 1.0,
  synchronizedNodes: 0,
};

const initialCredits: CreditBalance = {
  available: 0,
  pending: 0,
  earned: 0,
  spent: 0,
};

const defaultContributionSettings: ContributionSettings = {
  enabled: false,
  cpuLimit: 50,
  gpuEnabled: false,
  gpuLimit: 30,
  memoryLimit: 512,
  bandwidthLimit: 10,
  respectBattery: true,
  onlyWhenIdle: true,
  idleThreshold: 30,
  consentGiven: false,
  consentTimestamp: null,
};

export const useNetworkStore = create<NetworkState>()((set, get) => ({
  stats: initialStats,
  nodes: [],
  timeCrystal: initialTimeCrystal,
  credits: initialCredits,
  isConnected: false,
  isRelayConnected: false,
  isLoading: true,
  error: null,
  startTime: Date.now(),
  contributionSettings: defaultContributionSettings,
  isWASMReady: false,
  nodeId: null,
  relayNetworkState: null,
  connectedPeers: [],
  pendingTasks: [],
  persistedCredits: 0,
  persistedTasks: 0,
  persistedUptime: 0,
  // Firebase state
  isFirebaseConnected: false,
  isDemoMode: false,
  firebasePeers: [],
  firebaseStats: null,
  // Pending contribution credits (earned locally, not yet synced to QDAG)
  pendingContributionCredits: 0,
  lastContributionSyncTime: Date.now(),
  contributionSyncInterval: 30000, // 30 seconds

  setStats: (stats) =>
    set((state) => ({ stats: { ...state.stats, ...stats } })),

  addNode: (node) =>
    set((state) => {
      const newNodes = [...state.nodes, node];
      return {
        nodes: newNodes,
        stats: {
          ...state.stats,
          totalNodes: newNodes.length,
          activeNodes: newNodes.filter((n) => n.status === 'active').length,
        },
      };
    }),

  removeNode: (nodeId) =>
    set((state) => {
      const newNodes = state.nodes.filter((n) => n.id !== nodeId);
      return {
        nodes: newNodes,
        stats: {
          ...state.stats,
          totalNodes: newNodes.length,
          activeNodes: newNodes.filter((n) => n.status === 'active').length,
        },
      };
    }),

  updateNode: (nodeId, updates) =>
    set((state) => ({
      nodes: state.nodes.map((n) =>
        n.id === nodeId ? { ...n, ...updates } : n
      ),
    })),

  setTimeCrystal: (crystal) =>
    set((state) => ({
      timeCrystal: { ...state.timeCrystal, ...crystal },
    })),

  setCredits: (credits) =>
    set((state) => ({
      credits: { ...state.credits, ...credits },
    })),

  setConnected: (connected) =>
    set({ isConnected: connected, isLoading: false }),

  setLoading: (loading) => set({ isLoading: loading }),

  setError: (error) => set({ error, isLoading: false }),

  getUptime: () => {
    const state = get();
    return (Date.now() - state.startTime) / 1000;
  },

  setContributionSettings: (settings) =>
    set((state) => ({
      contributionSettings: { ...state.contributionSettings, ...settings },
    })),

  loadFromIndexedDB: async () => {
    try {
      const savedState = await storageService.loadState();
      if (savedState) {
        // Load pending contribution credits from IndexedDB (survives refresh)
        const pendingCredits = savedState.pendingContributionCredits || 0;
        const lastSyncTime = savedState.lastContributionSyncTime || 0;

        // Calculate total credits: synced (QDAG) + pending (local)
        const syncedCredits = savedState.creditsEarned;
        const displayCredits = syncedCredits + pendingCredits;

        set({
          persistedCredits: savedState.creditsEarned,
          persistedTasks: savedState.tasksCompleted,
          persistedUptime: savedState.totalUptime,
          nodeId: savedState.nodeId,
          pendingContributionCredits: pendingCredits,
          lastContributionSyncTime: lastSyncTime,
          contributionSettings: {
            ...defaultContributionSettings,
            consentGiven: savedState.consentGiven,
            consentTimestamp: savedState.consentTimestamp
              ? new Date(savedState.consentTimestamp)
              : null,
            cpuLimit: savedState.cpuLimit,
            gpuEnabled: savedState.gpuEnabled,
            gpuLimit: savedState.gpuLimit,
            respectBattery: savedState.respectBattery,
            onlyWhenIdle: savedState.onlyWhenIdle,
          },
          credits: {
            earned: displayCredits, // Include pending for display
            spent: savedState.creditsSpent,
            available: displayCredits - savedState.creditsSpent,
            pending: pendingCredits, // Track pending separately
          },
          stats: {
            ...initialStats,
            creditsEarned: displayCredits,
            tasksCompleted: savedState.tasksCompleted,
          },
        });
        console.log('[EdgeNet] Loaded persisted state:', syncedCredits, 'rUv synced,', pendingCredits, 'rUv pending');
      }
    } catch (error) {
      console.error('[EdgeNet] Failed to load from IndexedDB:', error);
    }
  },

  saveToIndexedDB: async () => {
    const state = get();
    try {
      // CRITICAL: Save synced credits separately from pending
      // creditsEarned = total displayed - pending = QDAG-synced portion
      // pendingContributionCredits = credits not yet synced to QDAG
      // On load: display = creditsEarned + pendingContributionCredits
      // This prevents double-counting on refresh
      const syncedCredits = Math.max(0, state.credits.earned - state.pendingContributionCredits);

      await storageService.saveState({
        id: 'primary',
        nodeId: state.nodeId,
        creditsEarned: syncedCredits, // Only QDAG-synced credits
        creditsSpent: state.credits.spent,
        tasksCompleted: state.stats.tasksCompleted,
        tasksSubmitted: 0,
        totalUptime: state.stats.uptime + state.persistedUptime,
        lastActiveTimestamp: Date.now(),
        consentGiven: state.contributionSettings.consentGiven,
        consentTimestamp: state.contributionSettings.consentTimestamp?.getTime() || null,
        cpuLimit: state.contributionSettings.cpuLimit,
        gpuEnabled: state.contributionSettings.gpuEnabled,
        gpuLimit: state.contributionSettings.gpuLimit,
        respectBattery: state.contributionSettings.respectBattery,
        onlyWhenIdle: state.contributionSettings.onlyWhenIdle,
        pendingContributionCredits: state.pendingContributionCredits,
        lastContributionSyncTime: state.lastContributionSyncTime,
      });
    } catch (error) {
      console.error('[EdgeNet] Failed to save to IndexedDB:', error);
    }
  },

  giveConsent: () => {
    set((state) => ({
      contributionSettings: {
        ...state.contributionSettings,
        consentGiven: true,
        consentTimestamp: new Date(),
      },
    }));
    get().saveToIndexedDB();
    console.log('[EdgeNet] User consent given for contribution');
  },

  revokeConsent: async () => {
    const { stopContributing } = get();
    stopContributing();
    set((state) => ({
      contributionSettings: {
        ...state.contributionSettings,
        consentGiven: false,
        consentTimestamp: null,
        enabled: false,
      },
    }));
    await storageService.clear();
    console.log('[EdgeNet] User consent revoked, data cleared');
  },

  initializeEdgeNet: async () => {
    try {
      set({ isLoading: true, error: null });
      console.log('[EdgeNet] Initializing...');

      // Load persisted state from IndexedDB first
      await get().loadFromIndexedDB();

      // Connect to Firebase for real-time network data (runs in parallel)
      get().connectToFirebase().catch((err) => {
        console.warn('[EdgeNet] Firebase connection failed, using demo mode:', err);
      });

      // Initialize WASM module
      await edgeNetService.init();
      const isWASMReady = edgeNetService.isWASMAvailable();
      set({ isWASMReady });

      if (isWASMReady) {
        console.log('[EdgeNet] WASM module ready');
        const node = await edgeNetService.createNode();
        if (node) {
          const nodeId = node.nodeId();
          set({ nodeId });
          console.log('[EdgeNet] Node created:', nodeId);
          edgeNetService.enableTimeCrystal(8);

          // Auto-start if consent was previously given
          const state = get();
          if (state.contributionSettings.consentGiven) {
            edgeNetService.startNode();
            set((s) => ({
              contributionSettings: { ...s.contributionSettings, enabled: true },
            }));
            console.log('[EdgeNet] Auto-started from previous session');

            // Auto-connect to relay
            setTimeout(() => {
              get().connectToRelay();
            }, 1000);
          }
        }
      }

      set({ isConnected: true, isLoading: false });
      console.log('[EdgeNet] Initialization complete');
    } catch (error) {
      console.error('[EdgeNet] Initialization failed:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to initialize',
        isLoading: false,
      });
    }
  },

  startContributing: async () => {
    const { contributionSettings, isWASMReady, nodeId } = get();
    if (!contributionSettings.consentGiven) {
      console.warn('[EdgeNet] Cannot start without consent');
      return;
    }

    // Start WASM node
    if (isWASMReady) {
      edgeNetService.startNode();
      console.log('[EdgeNet] Started WASM node');
    }

    set((state) => ({
      contributionSettings: { ...state.contributionSettings, enabled: true },
    }));

    // Connect to relay for distributed network
    if (nodeId) {
      const connected = await get().connectToRelay();
      if (connected) {
        console.log('[EdgeNet] Connected to distributed network');
      }
    }

    get().saveToIndexedDB();
    console.log('[EdgeNet] Started contributing');
  },

  stopContributing: () => {
    // Pause WASM node
    edgeNetService.pauseNode();

    // Disconnect from relay
    get().disconnectFromRelay();

    set((state) => ({
      contributionSettings: { ...state.contributionSettings, enabled: false },
    }));
    get().saveToIndexedDB();
    console.log('[EdgeNet] Stopped contributing');
  },

  updateRealStats: () => {
    const state = get();
    const sessionUptime = (Date.now() - state.startTime) / 1000;
    const totalUptime = sessionUptime + state.persistedUptime;
    const { isWASMReady, contributionSettings } = state;

    // Process epoch if contributing (advances WASM state)
    if (isWASMReady && contributionSettings.enabled) {
      edgeNetService.processEpoch();
      edgeNetService.stepCapabilities(1.0);
      edgeNetService.recordPerformance(0.95, 100);

      // Submit demo tasks periodically (every ~5 seconds) and process them
      if (Math.floor(sessionUptime) % 5 === 0) {
        edgeNetService.submitDemoTask();
      }
      // Process any queued tasks to earn credits
      edgeNetService.processNextTask().catch(() => {
        // No tasks available is normal
      });
    }

    // Get REAL stats from WASM node
    const realStats = edgeNetService.getStats();
    const timeCrystalSync = edgeNetService.getTimeCrystalSync();
    const networkFitness = edgeNetService.getNetworkFitness();

    // Debug: Log raw stats periodically
    if (realStats && Math.floor(sessionUptime) % 10 === 0) {
      console.log('[EdgeNet] Raw WASM stats:', {
        ruv_earned: realStats.ruv_earned?.toString(),
        tasks_completed: realStats.tasks_completed?.toString(),
        multiplier: realStats.multiplier,
        reputation: realStats.reputation,
        timeCrystalSync,
        networkFitness,
      });
    }

    if (realStats) {
      // Get task counts from WASM (credits are managed by creditsService)
      const sessionTasks = Number(realStats.tasks_completed);
      const totalTasks = state.persistedTasks + sessionTasks;

      // Calculate node count from various sources (relay, firebase, local)
      const relayNodes = state.relayNetworkState?.activeNodes || 0;
      const firebaseNodes = state.firebaseStats?.activePeers || 0;
      const localNodes = contributionSettings.enabled ? 1 : 0;
      const networkNodeCount = Math.max(relayNodes, firebaseNodes, localNodes);

      // Calculate total compute - TFLOPS scales with network nodes
      // Base compute per node: networkFitness * cpuLimit factor
      // Each additional node adds ~0.3-0.8 TFLOPS depending on their capability
      const baseComputePerNode = networkFitness * (contributionSettings.cpuLimit / 100);
      const networkMultiplier = Math.max(1, networkNodeCount);
      const totalCompute = Math.round(baseComputePerNode * networkMultiplier * 100) / 100;

      // Use creditsService values for credits (it handles earning), not WASM stats
      // WASM stats are used for network/task stats only
      const currentCredits = state.credits;

      set({
        stats: {
          totalNodes: networkNodeCount,
          activeNodes: networkNodeCount,
          totalCompute,
          // Use the actual credits.earned from creditsService, not WASM value
          creditsEarned: Math.round(currentCredits.earned * 100) / 100,
          tasksCompleted: totalTasks,
          uptime: Math.round(totalUptime * 10) / 10,
          latency: Math.round((1 - timeCrystalSync) * 100),
          bandwidth: Math.round(contributionSettings.bandwidthLimit * 10) / 10,
        },
        timeCrystal: {
          ...state.timeCrystal,
          phase: (state.timeCrystal.phase + 0.01) % 1,
          coherence: Math.round(timeCrystalSync * 1000) / 1000,
          entropy: Math.round((1 - timeCrystalSync * 0.8) * 1000) / 1000,
          synchronizedNodes: contributionSettings.enabled ? 1 : 0,
        },
        // Don't overwrite credits here - creditsService manages them via subscription
        isConnected: isWASMReady || get().isRelayConnected,
        isLoading: false,
      });

      // Save to IndexedDB periodically (every 10 seconds worth of updates)
      if (Math.floor(sessionUptime) % 10 === 0) {
        get().saveToIndexedDB();
      }

      // Check if we should sync pending credits to relay (every 30 seconds)
      const timeSinceLastSync = Date.now() - state.lastContributionSyncTime;
      if (state.pendingContributionCredits > 0 && timeSinceLastSync >= state.contributionSyncInterval) {
        get().syncContributionToRelay();
      }
    } else {
      // WASM not ready - show zeros but keep current credits (from creditsService)
      set({
        stats: {
          ...state.stats,
          totalNodes: 0,
          activeNodes: 0,
          totalCompute: 0,
          uptime: Math.round(totalUptime * 10) / 10,
          // Use current credits.earned, not reset to persistedCredits
          creditsEarned: Math.round(state.credits.earned * 100) / 100,
          tasksCompleted: state.persistedTasks,
        },
        // Don't overwrite credits - creditsService manages them
        isConnected: false,
        isLoading: !isWASMReady,
      });
    }
  },

  connectToRelay: async () => {
    const state = get();
    if (!state.nodeId) {
      console.warn('[EdgeNet] Cannot connect to relay without node ID');
      return false;
    }

    // Set up relay event handlers
    relayClient.setHandlers({
      onConnected: (_nodeId, networkState, peers) => {
        console.log('[EdgeNet] Connected to relay, peers:', peers.length);
        set({
          isRelayConnected: true,
          relayNetworkState: networkState,
          connectedPeers: peers,
          stats: {
            ...get().stats,
            activeNodes: networkState.activeNodes + 1, // Include ourselves
            totalNodes: networkState.totalNodes + 1,
          },
          timeCrystal: {
            ...get().timeCrystal,
            phase: networkState.timeCrystalPhase,
            synchronizedNodes: networkState.activeNodes + 1,
          },
        });
      },

      onDisconnected: () => {
        console.log('[EdgeNet] Disconnected from relay');
        set({
          isRelayConnected: false,
          connectedPeers: [],
        });
      },

      onNodeJoined: (nodeId, totalNodes) => {
        console.log('[EdgeNet] Peer joined:', nodeId);
        set((s) => ({
          connectedPeers: [...s.connectedPeers, nodeId],
          stats: { ...s.stats, activeNodes: totalNodes, totalNodes },
          timeCrystal: { ...s.timeCrystal, synchronizedNodes: totalNodes },
        }));
      },

      onNodeLeft: (nodeId, totalNodes) => {
        console.log('[EdgeNet] Peer left:', nodeId);
        set((s) => ({
          connectedPeers: s.connectedPeers.filter((id) => id !== nodeId),
          stats: { ...s.stats, activeNodes: totalNodes, totalNodes },
          timeCrystal: { ...s.timeCrystal, synchronizedNodes: totalNodes },
        }));
      },

      onTaskAssigned: (task) => {
        console.log('[EdgeNet] Task assigned:', task.id);
        set((s) => ({
          pendingTasks: [...s.pendingTasks, task],
        }));
        // Auto-process the task
        get().processAssignedTask(task);
      },

      onCreditEarned: (amount, taskId) => {
        const ruvAmount = Number(amount) / 1e9; // Convert from nanoRuv
        console.log('[EdgeNet] Credit earned:', ruvAmount, 'rUv for task', taskId);

        const currentState = get();
        const newPending = currentState.pendingContributionCredits + ruvAmount;

        // Add to pending contribution credits (local, not yet synced to QDAG)
        const newCredits = {
          earned: currentState.credits.earned + ruvAmount,
          available: currentState.credits.available + ruvAmount,
          spent: currentState.credits.spent,
          pending: newPending,
        };

        set((s) => ({
          credits: newCredits,
          pendingContributionCredits: newPending,
          stats: {
            ...s.stats,
            creditsEarned: newCredits.earned,
            tasksCompleted: s.stats.tasksCompleted + 1,
          },
        }));

        // Sync to QDAG - push update to network (source of truth)
        // Sign with WASM Ed25519 for secure, cross-platform verification
        const earnedNano = BigInt(Math.floor(newCredits.earned * 1e9));
        const spentNano = BigInt(Math.floor(newCredits.spent * 1e9));
        const signature = edgeNetService.signLedgerUpdate(earnedNano, spentNano);
        relayClient.updateLedger(earnedNano, spentNano, signature);

        // Check if we should trigger periodic sync (every 30 seconds)
        const timeSinceLastSync = Date.now() - currentState.lastContributionSyncTime;
        if (timeSinceLastSync >= currentState.contributionSyncInterval) {
          get().syncContributionToRelay();
        }

        get().saveToIndexedDB();
      },

      onTimeCrystalSync: (phase, _timestamp, activeNodes) => {
        set((s) => ({
          timeCrystal: {
            ...s.timeCrystal,
            phase,
            synchronizedNodes: activeNodes,
            coherence: Math.min(1, activeNodes / 10), // Coherence increases with more nodes
          },
        }));
      },

      onError: (error) => {
        console.error('[EdgeNet] Relay error:', error);
        set({ error: error.message });
      },

      // QDAG + Local Credit Sync Logic
      // Rule: NEVER reduce credits below what user has locally
      // If local > QDAG, sync local UP to QDAG (not the reverse)
      onLedgerSync: (ledger) => {
        const qdagEarned = Number(ledger.earned) / 1e9;
        const qdagSpent = Number(ledger.spent) / 1e9;
        const currentState = get();
        const localPending = currentState.pendingContributionCredits;
        // CRITICAL FIX: credits.earned is the DISPLAY value which already includes pending
        // So totalLocalCredits IS credits.earned (don't add pending again!)
        // localSyncedCredits is the QDAG-confirmed portion (display minus pending)
        const totalLocalCredits = currentState.credits.earned;
        const localSyncedCredits = Math.max(0, totalLocalCredits - localPending);

        console.log('[EdgeNet] QDAG balance received:', {
          qdagEarned,
          qdagSpent,
          localSyncedCredits,
          localPending,
          totalLocalCredits,
          tasksCompleted: ledger.tasksCompleted || 0,
        });

        // KEY FIX: Compare total local credits vs QDAG
        // If local > QDAG, we have unsync'd credits that need to go UP to QDAG
        if (totalLocalCredits > qdagEarned) {
          // Local has MORE credits than QDAG - preserve local and sync UP
          const unsyncedCredits = totalLocalCredits - qdagEarned;
          console.log('[EdgeNet] Local credits (' + totalLocalCredits.toFixed(4) + ') > QDAG (' + qdagEarned.toFixed(4) + ')');
          console.log('[EdgeNet] Preserving local credits, syncing ' + unsyncedCredits.toFixed(4) + ' rUv to QDAG');

          // Keep total local as the displayed value
          set((s) => ({
            credits: {
              earned: totalLocalCredits,
              spent: Math.max(qdagSpent, s.credits.spent),
              available: totalLocalCredits - Math.max(qdagSpent, s.credits.spent),
              pending: unsyncedCredits, // These need to sync to QDAG
            },
            pendingContributionCredits: unsyncedCredits,
            stats: {
              ...s.stats,
              creditsEarned: totalLocalCredits,
              tasksCompleted: Math.max(ledger.tasksCompleted || 0, s.stats.tasksCompleted),
            },
          }));

          // Trigger sync to push local credits UP to QDAG
          get().syncContributionToRelay();
        } else if (qdagEarned > 0) {
          // QDAG has equal or more credits - use QDAG as authoritative
          console.log('[EdgeNet] QDAG is authoritative with', qdagEarned, 'rUv');

          // Display: earned = qdag + any new pending since sync
          const displayEarned = qdagEarned + localPending;
          set((s) => ({
            credits: {
              earned: displayEarned,
              spent: qdagSpent,
              available: displayEarned - qdagSpent,
              pending: localPending,
            },
            // Keep pending credits that were earned after QDAG sync
            pendingContributionCredits: localPending,
            lastContributionSyncTime: Date.now(),
            stats: {
              ...s.stats,
              creditsEarned: displayEarned,
              tasksCompleted: ledger.tasksCompleted || s.stats.tasksCompleted,
            },
          }));
        } else {
          // QDAG is 0 but local might have credits - preserve local
          if (totalLocalCredits > 0) {
            console.log('[EdgeNet] QDAG is 0, preserving local credits:', totalLocalCredits, 'rUv');
            set((s) => ({
              credits: {
                earned: totalLocalCredits,
                spent: s.credits.spent,
                available: totalLocalCredits - s.credits.spent,
                pending: totalLocalCredits,
              },
              pendingContributionCredits: totalLocalCredits,
              stats: {
                ...s.stats,
                creditsEarned: totalLocalCredits,
              },
            }));
            // Sync to QDAG
            get().syncContributionToRelay();
          } else {
            // Fresh start - no credits anywhere
            set((s) => ({
              credits: {
                earned: 0,
                spent: 0,
                available: 0,
                pending: 0,
              },
              pendingContributionCredits: 0,
              stats: {
                ...s.stats,
                creditsEarned: 0,
                tasksCompleted: ledger.tasksCompleted || s.stats.tasksCompleted,
              },
            }));
          }
        }

        // Save to IndexedDB as backup
        get().saveToIndexedDB();
        console.log('[EdgeNet] Credits synced - Display:', get().credits.earned.toFixed(4), 'rUv, Pending:', get().pendingContributionCredits.toFixed(4), 'rUv');
      },

      // Handle acknowledgment of contribution credits from relay
      onContributionAck: (ack) => {
        if (ack.success) {
          const acceptedRuv = Number(ack.creditsAccepted) / 1e9;
          const newBalanceRuv = Number(ack.newBalance) / 1e9;

          console.log('[EdgeNet] Contribution acknowledged:', {
            accepted: acceptedRuv,
            newBalance: newBalanceRuv,
          });

          // Relay accepted our contribution - update state
          // The new balance from QDAG becomes authoritative
          const currentState = get();
          const remainingPending = Math.max(0, currentState.pendingContributionCredits - acceptedRuv);

          set({
            credits: {
              earned: newBalanceRuv + remainingPending,
              spent: currentState.credits.spent,
              available: newBalanceRuv + remainingPending - currentState.credits.spent,
              pending: remainingPending,
            },
            pendingContributionCredits: remainingPending,
            lastContributionSyncTime: Date.now(),
          });

          get().saveToIndexedDB();
          console.log('[EdgeNet] Contribution synced to QDAG, remaining pending:', remainingPending, 'rUv');
        } else {
          console.warn('[EdgeNet] Contribution not acknowledged, will retry');
        }
      },
    });

    // Get publicKey from identityStore (same key = same credits everywhere)
    const identityState = useIdentityStore.getState();
    const publicKey = identityState.identity?.publicKey;

    if (!publicKey) {
      console.warn('[EdgeNet] No identity found - generate one first for QDAG credits');
    }

    // Connect to the relay with publicKey for QDAG identity
    const connected = await relayClient.connect(state.nodeId, publicKey);
    if (connected) {
      console.log('[EdgeNet] Relay connection established with identity:', publicKey?.slice(0, 16) || 'none');
      // Request QDAG ledger sync (QDAG is source of truth)
      if (publicKey) {
        setTimeout(() => {
          relayClient.requestLedgerSync(publicKey);
          console.log('[EdgeNet] Requested QDAG balance for identity:', publicKey.slice(0, 16));
        }, 500);
      }
    } else {
      console.warn('[EdgeNet] Failed to connect to relay');
    }
    return connected;
  },

  disconnectFromRelay: () => {
    relayClient.disconnect();
    set({
      isRelayConnected: false,
      connectedPeers: [],
      pendingTasks: [],
    });
  },

  processAssignedTask: async (task) => {
    const state = get();
    if (!state.isWASMReady) {
      console.warn('[EdgeNet] Cannot process task - WASM not ready');
      return;
    }

    try {
      console.log('[EdgeNet] Processing task:', task.id, task.taskType);

      // Process the task using WASM
      const result = await edgeNetService.submitTask(
        task.taskType,
        task.payload,
        task.maxCredits
      );

      // Process the task in WASM node
      await edgeNetService.processNextTask();

      // Report completion to relay
      const reward = task.maxCredits / BigInt(2); // Earn half the max credits
      relayClient.completeTask(task.id, task.submitter, result, reward);

      // Remove from pending
      set((s) => ({
        pendingTasks: s.pendingTasks.filter((t) => t.id !== task.id),
      }));

      console.log('[EdgeNet] Task completed:', task.id);
    } catch (error) {
      console.error('[EdgeNet] Task processing failed:', error);
    }
  },

  // Firebase real-time data methods
  connectToFirebase: async () => {
    console.log('[EdgeNet] Connecting to Firebase for real-time peer data...');

    // Set up event handlers before connecting
    firebaseDataService.on('connected', () => {
      set({
        isFirebaseConnected: true,
        isDemoMode: false,
      });
      console.log('[EdgeNet] Firebase connected');
    });

    firebaseDataService.on('disconnected', () => {
      set({
        isFirebaseConnected: false,
        firebasePeers: [],
      });
      console.log('[EdgeNet] Firebase disconnected');
    });

    firebaseDataService.on('stats-updated', (statsArg: unknown) => {
      const stats = statsArg as NetworkStatistics;
      const state = get();
      set({
        firebaseStats: stats,
        firebasePeers: stats.peers,
        isDemoMode: firebaseDataService.isInDemoMode(),
        // Update main stats with Firebase data when we have more peers from Firebase
        stats: {
          ...state.stats,
          // Use Firebase peer count if greater than relay count
          totalNodes: Math.max(state.stats.totalNodes, stats.totalPeers),
          activeNodes: Math.max(state.stats.activeNodes, stats.activePeers),
        },
        timeCrystal: {
          ...state.timeCrystal,
          // Sync node count with Firebase if greater
          synchronizedNodes: Math.max(state.timeCrystal.synchronizedNodes, stats.activePeers),
        },
      });
    });

    firebaseDataService.on('peer-joined', (peerArg: unknown) => {
      const peer = peerArg as PeerInfo;
      set((s) => ({
        firebasePeers: [...s.firebasePeers.filter(p => p.id !== peer.id), peer],
      }));
      console.log('[EdgeNet] Firebase peer joined:', peer.id.slice(0, 8));
    });

    firebaseDataService.on('peer-left', (peerIdArg: unknown) => {
      const peerId = peerIdArg as string;
      set((s) => ({
        firebasePeers: s.firebasePeers.filter(p => p.id !== peerId),
      }));
      console.log('[EdgeNet] Firebase peer left:', peerId.slice(0, 8));
    });

    firebaseDataService.on('error', (errorArg: unknown) => {
      const error = errorArg as Error;
      console.error('[EdgeNet] Firebase error:', error);
    });

    // Connect to Firebase
    const connected = await firebaseDataService.connect();

    if (!connected) {
      // Running in demo mode
      set({ isDemoMode: true });
      console.log('[EdgeNet] Running in Firebase demo mode');
    }

    return connected;
  },

  disconnectFromFirebase: () => {
    firebaseDataService.disconnect();
    set({
      isFirebaseConnected: false,
      isDemoMode: false,
      firebasePeers: [],
      firebaseStats: null,
    });
  },

  setFirebaseStats: (stats: NetworkStatistics) => {
    set({ firebaseStats: stats });
  },

  addFirebasePeer: (peer: PeerInfo) => {
    set((s) => ({
      firebasePeers: [...s.firebasePeers.filter(p => p.id !== peer.id), peer],
    }));
  },

  removeFirebasePeer: (peerId: string) => {
    set((s) => ({
      firebasePeers: s.firebasePeers.filter(p => p.id !== peerId),
    }));
  },

  clearLocalData: () => {
    // Clear IndexedDB
    if (typeof indexedDB !== 'undefined') {
      indexedDB.deleteDatabase('edge-net-store');
    }
    // Clear localStorage
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('edge-net-joined-networks');
      localStorage.removeItem('edge-net-identity');
    }
    // Reset state
    set({
      credits: { available: 0, pending: 0, earned: 0, spent: 0 },
      stats: {
        totalNodes: 0,
        activeNodes: 0,
        totalCompute: 0,
        creditsEarned: 0,
        tasksCompleted: 0,
        uptime: 0,
        latency: 0,
        bandwidth: 0,
      },
      persistedCredits: 0,
      persistedTasks: 0,
      persistedUptime: 0,
      pendingContributionCredits: 0,
      lastContributionSyncTime: Date.now(),
      nodeId: null,
      contributionSettings: {
        enabled: false,
        cpuLimit: 50,
        gpuEnabled: false,
        gpuLimit: 50,
        memoryLimit: 50,
        bandwidthLimit: 50,
        respectBattery: true,
        onlyWhenIdle: true,
        idleThreshold: 60,
        consentGiven: false,
        consentTimestamp: null,
      },
    });
    console.log('[NetworkStore] All local data cleared');
  },

  // Sync pending contribution credits to QDAG relay
  syncContributionToRelay: async () => {
    const state = get();
    const pendingCredits = state.pendingContributionCredits;

    if (pendingCredits <= 0) {
      console.log('[EdgeNet] No pending credits to sync');
      return;
    }

    if (!relayClient.isConnected()) {
      console.log('[EdgeNet] Relay not connected, cannot sync pending credits');
      return;
    }

    try {
      console.log('[EdgeNet] Syncing pending credits to QDAG:', pendingCredits, 'rUv');

      // Calculate contribution time since last sync
      const now = Date.now();
      const timeSinceLastSync = now - state.lastContributionSyncTime;
      const contributionSeconds = Math.round(timeSinceLastSync / 1000);
      const cpuUsage = state.contributionSettings.cpuLimit;

      // Use the new reportContribution method
      relayClient.reportContribution(contributionSeconds, cpuUsage, pendingCredits);

      // Update last sync time (pending will be cleared when QDAG confirms via onContributionAck)
      set({
        lastContributionSyncTime: now,
      });

      console.log('[EdgeNet] Contribution sync request sent to QDAG:', {
        seconds: contributionSeconds,
        credits: pendingCredits.toFixed(6),
        cpu: cpuUsage,
      });

      // Save state with updated sync time
      get().saveToIndexedDB();
    } catch (error) {
      console.error('[EdgeNet] Failed to sync pending credits to QDAG:', error);
    }
  },

  // Add credits to pending contribution (for local earnings not yet synced)
  addPendingContribution: (amount: number) => {
    if (amount <= 0) return;

    const state = get();
    const newPending = state.pendingContributionCredits + amount;

    set({
      pendingContributionCredits: newPending,
      credits: {
        ...state.credits,
        earned: state.credits.earned + amount,
        available: state.credits.available + amount,
        pending: newPending,
      },
      stats: {
        ...state.stats,
        creditsEarned: state.stats.creditsEarned + amount,
      },
    });

    console.log('[EdgeNet] Added pending contribution:', amount, 'rUv, total pending:', newPending, 'rUv');

    // Check if we should trigger periodic sync
    const timeSinceLastSync = Date.now() - state.lastContributionSyncTime;
    if (timeSinceLastSync >= state.contributionSyncInterval) {
      get().syncContributionToRelay();
    }

    get().saveToIndexedDB();
  },
}));
